import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { PageHeader } from '../components/PageHeader';
import { ApiError } from '../api/client';
import { platformApi, type Organization } from '../api/endpoints';
import { useToast } from '../app/ToastProvider';

type Mode = 'organization' | 'admin' | 'mikrotik';

export function PlatformPage() {
  const [mode, setMode] = useState<Mode | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [editingExisting, setEditingExisting] = useState(false);
  const [loadingRecord] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [active, setActive] = useState(true);
  const [tlsVerify, setTlsVerify] = useState(true);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data = [], isError } = useQuery({
    queryKey: ['platform-organizations'],
    queryFn: platformApi.organizations,
  });

  const close = () => {
    setMode(null);
    setOrganization(null);
    setForm({});
    setEditingExisting(false);
    setActive(true);
    setTlsVerify(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'organization') {
        return platformApi.createOrganization({ name: form.name, slug: form.slug });
      }
      if (!organization) throw new Error('Organization is required.');
      if (mode === 'admin') {
        return platformApi.saveAdmin(organization.id, {
          username: form.username,
          ...(form.password ? { password: form.password } : {}),
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          isActive: active,
        });
      }
      return platformApi.saveMikrotik(organization.id, {
        name: form.name,
        baseUrl: form.baseUrl,
        username: form.username,
        ...(form.password ? { password: form.password } : {}),
        tlsVerify,
        isActive: active,
      });
    },
    onSuccess: async ({ message }) => {
      toast.success(message);
      close();
      await queryClient.invalidateQueries({ queryKey: ['platform-organizations'] });
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'The configuration could not be saved.');
    },
  });

  const openOrganization = () => {
    setForm({});
    setEditingExisting(false);
    setMode('organization');
  };

  const openAdmin = async (org: Organization) => {
    setOrganization(org);
    setMode('admin');
    const admin = org.admin;
    setEditingExisting(Boolean(admin));
    setActive(admin?.isActive ?? true);
    setForm(admin ? {
      username: admin.username,
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      password: '',
    } : {});
  };

  const openMikrotik = async (org: Organization) => {
    setOrganization(org);
    setMode('mikrotik');
    const config = org.mikrotik;
    setEditingExisting(Boolean(config));
    setActive(config?.isActive ?? true);
    setTlsVerify(config?.tlsVerify ?? true);
    setForm(config ? {
      name: config.name,
      baseUrl: config.baseUrl,
      username: config.username,
      password: '',
    } : {});
  };

  const field = (name: string, label: string, type = 'text') => (
    <TextField
      type={type}
      label={label}
      value={form[name] ?? ''}
      onChange={(event) => setForm({ ...form, [name]: event.target.value })}
      required={name !== 'password' || !editingExisting}
      error={save.error instanceof ApiError && Boolean(save.error.byField()[name])}
      helperText={
        save.error instanceof ApiError && save.error.byField()[name]
          ? save.error.byField()[name]
          : name === 'password' && editingExisting
            ? 'Leave blank to keep the current password.'
            : undefined
      }
      fullWidth
    />
  );

  const title = mode === 'organization'
    ? 'New organization'
    : (editingExisting ? 'Edit ' : 'Create ') +
      (mode === 'admin' ? 'admin for ' : 'MikroTik for ') +
      (organization?.name ?? '');

  return (
    <>
      <PageHeader
        title="Platform Administration"
        description="Each organization has one primary administrator and one MikroTik configuration."
        actions={<Button variant="contained" onClick={openOrganization}>New organization</Button>}
      />
      {isError ? <Alert severity="error">Organizations could not be loaded.</Alert> : null}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>MikroTik</TableCell>
              <TableCell align="right">Configuration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((org) => (
              <TableRow key={org.id}>
                <TableCell>{org.name}</TableCell>
                <TableCell>{org.slug}</TableCell>
                <TableCell>{org.admin ? 'Configured' : 'Not configured'}</TableCell>
                <TableCell>{org.mikrotik ? 'Configured' : 'Not configured'}</TableCell>
                <TableCell align="right">
                  <Button onClick={() => void openAdmin(org)}>
                    {org.admin ? 'Edit Admin' : 'Add Admin'}
                  </Button>
                  <Button onClick={() => void openMikrotik(org)}>
                    {org.mikrotik ? 'Edit MikroTik' : 'Add MikroTik'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={Boolean(mode)} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{title}</DialogTitle>
        <form onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
          <DialogContent>
          {loadingRecord ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {save.isError ? (
                <Alert severity="error">
                  {save.error instanceof ApiError ? save.error.message : 'The configuration could not be saved.'}
                </Alert>
              ) : null}
              {mode === 'organization' ? (
                <>{field('name', 'Name')}{field('slug', 'Slug')}</>
              ) : mode === 'admin' ? (
                <>
                  {field('username', 'Username')}
                  {field('password', editingExisting ? 'New password' : 'Temporary password', 'password')}
                  {field('firstName', 'First name')}
                  {field('lastName', 'Last name')}
                  {field('email', 'Email', 'email')}
                  <FormControlLabel control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />} label="Active" />
                </>
              ) : (
                <>
                  {field('name', 'Connection name')}
                  {field('baseUrl', 'Router API URL')}
                  {field('username', 'Username')}
                  {field('password', editingExisting ? 'New password' : 'Password', 'password')}
                  <FormControlLabel control={<Switch checked={tlsVerify} onChange={(event) => setTlsVerify(event.target.checked)} />} label="Verify TLS certificate" />
                  <FormControlLabel control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />} label="Active" />
                </>
              )}
            </Stack>
          )}
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={close}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loadingRecord || save.isPending}>
              {editingExisting ? 'Save changes' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
